import { useQuery } from "@apollo/client";
import type React from "react";
import { createContext, useCallback, useContext, useEffect, useMemo } from "react";
import { gql } from "@medbillai/graphql-types";
import { errorHandling } from "../../lib/apollo/utils";
import { useAuth } from "../../lib/auth";
import { useAppState } from "../../lib/useAppState";

// Polling interval in milliseconds (30 seconds)
const POLLING_INTERVAL_MS = 30000;
export const getUnreadIssuesCountQuery = gql(/* GraphQL */`
  query GetUnreadIssues {
    me {
      id
      issues(
        filter: {
          firstUnseenEvent: { exists: true }
          closedAt: { exists: false }
        }
      ) {
        id
        firstUnseenEvent {
          id
        }
      }
    }
  }
`);
interface IssueEventsContextInterface {
  unreadIssuesCount: number;
  refetchUnreadIssuesCount: () => void;
}
const initialContext: IssueEventsContextInterface = {
  unreadIssuesCount: 0,
  refetchUnreadIssuesCount: () => {
    throw new Error("Context not initialized");
  }
};
const IssueEventsContext = createContext<IssueEventsContextInterface>(initialContext);
export interface IssueEventsProviderProps {
  children: React.ReactNode;
}

/**
 * Keeps count of unread Issues and provides a function to refetch the count.
 * Uses Apollo's built-in polling mechanism to keep the count up to date.
 */
export function IssueEventsProvider({
  children
}: IssueEventsProviderProps) {
  const {
    isAuthenticated,
    isReady
  } = useAuth();
  const {
    isActive
  } = useAppState();
  const {
    data,
    refetch,
    startPolling,
    stopPolling
  } = useQuery(getUnreadIssuesCountQuery, {
    context: errorHandling("caller"),
    skip: !isAuthenticated || !isReady,
    fetchPolicy: "network-only"
  });
  const refetchUnreadIssuesCount = useCallback(() => {
    void refetch();
  }, [refetch]);

  // Manually control polling based on app state
  useEffect(() => {
    if (isActive && isAuthenticated && isReady) {
      void refetch();
      startPolling(POLLING_INTERVAL_MS);
    } else {
      stopPolling();
    }
    return () => {
      stopPolling();
    };
  }, [isActive, isAuthenticated, isReady, startPolling, stopPolling, refetch]);
  const contextValue = useMemo<IssueEventsContextInterface>(() => ({
    unreadIssuesCount: data?.me?.issues?.length ?? 0,
    refetchUnreadIssuesCount
  }), [data?.me?.issues?.length, refetchUnreadIssuesCount]);
  return <IssueEventsContext.Provider value={contextValue} data-sentry-element="IssueEventsContext.Provider" data-sentry-component="IssueEventsProvider" data-sentry-source-file="IssueEventsProvider.tsx">
      {children}
    </IssueEventsContext.Provider>;
}
export const useIssueEvents = (context = IssueEventsContext): IssueEventsContextInterface => useContext(context);